import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { showToast } from "../../store/uiSlice";
import { adminApi } from "../../services/api";
import TeamLeadDetail from "./TeamLeadDetail";

const TeamLeadsList = () => {
  const dispatch = useDispatch();
  const [teamLeads, setTeamLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterType, setFilterType] = useState("all"); // all | conversion | retention
  const [selectedTLId, setSelectedTLId] = useState(null);

  const fetchTeamLeads = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await adminApi.get("/admin/team-leads");
      setTeamLeads(res.data?.teamLeads || []);
    } catch (e) {
      dispatch(
        showToast({ message: "Failed to load team leads", type: "error" }),
      );
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchTeamLeads();
  }, [fetchTeamLeads]);

  const visible = teamLeads.filter((tl) =>
    filterType === "all" ? true : tl.staffType === filterType,
  );

  const conversionCount = teamLeads.filter(
    (tl) => tl.staffType !== "retention",
  ).length;
  const retentionCount = teamLeads.filter(
    (tl) => tl.staffType === "retention",
  ).length;

  // If a TL is selected, render the detail view inline
  if (selectedTLId) {
    return (
      <TeamLeadDetail
        teamLeadId={selectedTLId}
        onBack={() => {
          setSelectedTLId(null);
          fetchTeamLeads();
        }}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-10 h-10 border-[3px] border-[#1e3a5f] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-5 border-b-2 border-[#e2e8f0] bg-[#f8fafc]">
        <div>
          <h2 className="font-syne text-xl font-extrabold text-[#0b1e33] uppercase tracking-tight">
            TEAM LEADS
          </h2>
          <p className="text-[11px] text-[#475569] font-mono font-extrabold mt-1 uppercase tracking-wider">
            {teamLeads.length} team lead{teamLeads.length !== 1 ? "s" : ""} ·{" "}
            {conversionCount} conversion · {retentionCount} retention
          </p>
        </div>
        <button
          onClick={fetchTeamLeads}
          className="px-4 py-2 rounded-xl bg-[#1e3a5f] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[2px] border-[#0b1e33]"
        >
          <i className="fas fa-sync-alt mr-1.5" /> REFRESH
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-b-2 border-[#e2e8f0] bg-[#f8fafc]">
        <span className="text-[10px] text-[#475569] font-mono font-extrabold uppercase tracking-wider">
          FILTER:
        </span>
        {[
          { id: "all", label: "ALL" },
          { id: "conversion", label: "CONVERSION" },
          { id: "retention", label: "RETENTION" },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilterType(f.id)}
            className={`px-4 py-2 rounded-xl text-[10px] font-mono font-extrabold uppercase tracking-wider transition-all whitespace-nowrap ${
              filterType === f.id
                ? f.id === "retention"
                  ? "bg-[#d97706] text-white shadow-md"
                  : f.id === "conversion"
                    ? "bg-[#059669] text-white shadow-md"
                    : "bg-[#1e3a5f] text-white shadow-md"
                : "bg-white border-2 border-[#e2e8f0] text-[#64748b] hover:border-[#1e3a5f] hover:text-[#1e3a5f]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Empty state */}
      {visible.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-20 h-20 rounded-2xl bg-[#f1f5f9] flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-user-tie text-[#94a3b8] text-3xl" />
          </div>
          <p className="text-[#475569] font-syne font-extrabold text-base uppercase tracking-wider mb-1">
            NO TEAM LEADS
          </p>
          <p className="text-[#64748b] font-mono text-xs font-bold">
            Mark a staff member as TEAM LEAD in Staff Management to see them here
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-5">
          {visible.map((tl) => {
            const isRetention = tl.staffType === "retention";
            return (
              <button
                key={tl.id}
                onClick={() => setSelectedTLId(tl.id)}
                className="text-left bg-white border-2 border-[#e2e8f0] rounded-2xl p-5 hover:border-[#1e3a5f] hover:shadow-lg transition-all group"
              >
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-md ${
                      isRetention
                        ? "bg-gradient-to-br from-[#d97706] to-[#b45309] shadow-[#d97706]/25"
                        : "bg-gradient-to-br from-[#059669] to-[#047857] shadow-[#059669]/25"
                    }`}
                  >
                    <span className="text-sm font-extrabold text-white">
                      {tl.firstName?.[0] || ""}
                      {tl.lastName?.[0] || ""}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-syne text-sm font-extrabold text-[#0f172a] truncate group-hover:text-[#1e3a5f] transition-colors">
                      {tl.firstName} {tl.lastName}
                    </div>
                    <div className="text-[10px] text-[#64748b] font-mono font-bold truncate">
                      {tl.email}
                    </div>
                  </div>
                </div>

                {/* Staff type pill */}
                <div className="flex items-center gap-2 mb-4">
                  {isRetention ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#fef3c7] text-[#92400e] text-[10px] font-mono font-extrabold uppercase tracking-wider border-2 border-[#fcd34d]">
                      <i className="fas fa-lock text-[9px]" /> RETENTION
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#ecfdf5] text-[#059669] text-[10px] font-mono font-extrabold uppercase tracking-wider border-2 border-[#a7f3d0]">
                      <i className="fas fa-rotate text-[9px]" /> CONVERSION
                    </span>
                  )}
                  {!tl.isActive && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-[#fef2f2] text-[#dc2626] text-[9px] font-mono font-extrabold uppercase tracking-wider border border-[#fecaca]">
                      INACTIVE
                    </span>
                  )}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="bg-[#f8fafc] border-2 border-[#e2e8f0] rounded-xl p-3">
                    <div className="text-[9px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider mb-1">
                      TEAM
                    </div>
                    <div className="font-extrabold text-lg text-[#0f172a]">
                      {tl.teamCount ?? 0}
                    </div>
                  </div>
                  <div className="bg-[#f8fafc] border-2 border-[#e2e8f0] rounded-xl p-3">
                    <div className="text-[9px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider mb-1">
                      LEADS
                    </div>
                    <div className="font-extrabold text-lg text-[#0f172a]">
                      {tl.leadCount ?? 0}
                    </div>
                  </div>
                </div>

                {/* Footer CTA */}
                <div className="flex items-center justify-between pt-2 border-t-2 border-[#f1f5f9]">
                  <span className="text-[10px] text-[#64748b] font-mono font-bold">
                    Click to manage
                  </span>
                  <i className="fas fa-arrow-right text-[#1e3a5f] text-xs group-hover:translate-x-1 transition-transform" />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TeamLeadsList;